import React from "react";
import { Calendar, Building2 } from "lucide-react";
import { motion } from "motion/react";
import { animated } from "@react-spring/web";
import { Card } from "./ui/card";
import { useLocalizedContent } from "../context/LanguageContext";
import { useParallax } from "../hooks/useParallax";
import { useTilt } from "../hooks/useTilt";
import SectionParallax from "./SectionParallax";

const sectionVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 140, damping: 18 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.08 },
  },
};

const Experience = () => {
  const { experiences, experience } = useLocalizedContent();
  const contentShift = useParallax(0.02);

  return (
    <motion.section
      id="experience"
      className="section-fade relative py-24 overflow-hidden"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900 to-black"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/10 via-transparent to-transparent"></div>
      <SectionParallax variant="teal" />

      <div
        className="relative z-10 container mx-auto px-6 lg:px-12 transition-transform duration-500"
        style={{ transform: `translate3d(0, ${contentShift}px, 0)` }}
      >
        <motion.div variants={sectionVariants}>
          {/* Section Header */}
          <motion.div className="text-center mb-16" variants={itemVariants}>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase bg-blue-500/10 text-blue-300 border border-blue-500/20 mb-4">
              {experience.badge}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {experience.heading}
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              {experience.description}
            </p>
          </motion.div>

          {/* Experience Timeline */}
          <div className="max-w-5xl mx-auto space-y-6">
            {experiences.map((exp, index) => (
              <ExperienceCard key={exp.id} exp={exp} index={index} />
            ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

const ExperienceCard = ({ exp, index }) => {
  const tilt = useTilt({ max: 10, scale: 1.02 });

  return (
    <animated.div
      ref={tilt.ref}
      style={tilt.style}
      {...tilt.bind}
      className="will-change-transform"
    >
      <Card className="group relative p-8 bg-white/5 backdrop-blur-xl border border-white/10 hover:border-blue-500/50 rounded-2xl transition-all duration-300 hover:transform hover:scale-[1.02]">
        {/* Glow Effect on Hover */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 to-cyan-500/0 group-hover:from-blue-500/5 group-hover:to-cyan-500/5 transition-all duration-300"></div>

        <motion.div className="relative z-10" variants={cardVariants}>
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Company Logo Placeholder */}
            <motion.div className="flex-shrink-0" variants={itemVariants}>
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-blue-300" />
              </div>
            </motion.div>

            {/* Content */}
            <div className="flex-1 space-y-3">
              {/* Role & Company */}
              <motion.div variants={itemVariants}>
                <h3 className="text-2xl font-semibold text-white group-hover:text-blue-300 transition-colors">
                  {exp.role}
                </h3>
                <p className="text-white/60 text-lg">{exp.company}</p>
              </motion.div>

              {/* Period */}
              <motion.div
                className="flex items-center gap-2 text-white/40"
                variants={itemVariants}
              >
                <Calendar className="w-4 h-4" />
                <span className="text-sm">{exp.period}</span>
              </motion.div>

              {/* Highlights */}
              <motion.ul className="space-y-2 mt-4" variants={cardVariants}>
                {exp.highlights.map((highlight, idx) => (
                  <motion.li
                    key={idx}
                    className="flex items-start gap-3 text-white/60"
                    variants={itemVariants}
                  >
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></span>
                    <span className="text-sm leading-relaxed">{highlight}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
        </motion.div>

        {/* Index Number */}
        <motion.div
          className="absolute top-6 right-6 text-6xl font-bold text-white/5"
          variants={itemVariants}
        >
          {String(index + 1).padStart(2, "0")}
        </motion.div>
      </Card>
    </animated.div>
  );
};

export default Experience;
